import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  registerB2BConfirmHost,
  type B2BConfirmResolver,
  type B2BConfirmSubject,
} from '@/lib/b2bConfirmGate';
import { formatPlanPrice, planDisplayName, planFromLookupKey } from '@/lib/plans';

interface B2BConfirmDialogProps {
  open: boolean;
  /** true = aktiv bestaetigt, false = jeder Abbruchweg (Cancel, X, Overlay). */
  onResult: (confirmed: boolean) => void;
  /** Was gebucht wird (A7-NEU-2). Fehlt es oder ist es kein Band, bleibt die Kaufzeile weg. */
  subject?: B2BConfirmSubject;
}

/**
 * Aktive Unternehmer-Bestaetigung vor dem Stripe-Redirect (Paket B
 * Umsatzsteuer). LG-Trier-Massstab: die Checkbox ist NIEMALS vorausgewaehlt
 * und wird bei jedem Oeffnen zurueckgesetzt; der Primaer-Button bleibt
 * disabled, bis sie aktiv gesetzt wurde.
 */
export const B2BConfirmDialog: React.FC<B2BConfirmDialogProps> = ({ open, onResult, subject }) => {
  const { t, i18n } = useTranslation('checkout');
  const checkboxId = useId();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  const parsed = subject ? planFromLookupKey(subject.lookupKey) : null;
  const purchase = parsed && subject
    ? t(parsed.cycle === 'yearly' ? 'b2bDialog.purchaseYearly' : 'b2bDialog.purchaseMonthly', {
        plan: planDisplayName(parsed.plan, i18n?.language, t),
        price: formatPlanPrice(parsed.plan, subject.currency, parsed.cycle, i18n?.language),
      })
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onResult(false); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('b2bDialog.title')}</DialogTitle>
          <DialogDescription>{t('b2bDialog.description')}</DialogDescription>
        </DialogHeader>

        {purchase && (
          <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium" data-testid="b2b-dialog-purchase">
            {purchase}
          </p>
        )}

        <div className="flex items-start gap-3 py-2">
          <Checkbox
            id={checkboxId}
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            className="mt-0.5"
          />
          <label htmlFor={checkboxId} className="text-sm leading-snug cursor-pointer">
            {t('b2bDialog.checkbox')}
          </label>
        </div>

        <p className="text-xs text-muted-foreground">{t('b2bDialog.taxHint')}</p>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onResult(false)}>
            {t('b2bDialog.cancel')}
          </Button>
          <Button disabled={!checked} onClick={() => onResult(true)}>
            {t('b2bDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Der (eine) global gemountete Host des B2B-Gates. Er abonniert Anfragen aus
 * `requestB2BConfirmation()` (lib/b2bConfirmGate.ts) und rendert den Dialog.
 * Gemountet in Layout.tsx neben AccessRevokedOverlay — alle handleUpgrade-
 * Aufrufer liegen innerhalb von Layout-Routen.
 */
export const B2BConfirmHost: React.FC = () => {
  const resolverRef = useRef<B2BConfirmResolver | null>(null);
  const [open, setOpen] = useState(false);
  // Remount-Schluessel: jede Anfrage bekommt einen FRISCHEN Dialog (key), die
  // Checkbox startet also garantiert unchecked. Der useEffect-Reset im Dialog
  // greift nur bei open-Wechseln — er deckt weder die Zweitanfrage bei bereits
  // offenem Dialog ab noch den Ein-Frame-Flash eines stehengebliebenen Hakens
  // nach Abbruch + Wiederoeffnen (useEffect laeuft erst nach dem Paint).
  const [requestSeq, setRequestSeq] = useState(0);
  const [subject, setSubject] = useState<B2BConfirmSubject | undefined>(undefined);

  useEffect(() => {
    const unregister = registerB2BConfirmHost((resolve, nextSubject) => {
      // Eine neue Anfrage verneint eine eventuell noch offene alte, damit
      // deren Promise nicht ewig haengt.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setSubject(nextSubject);
      setRequestSeq((s) => s + 1);
      setOpen(true);
    });
    return () => {
      unregister();
      // Unmount mit offener Anfrage = Abbruch, kein haengendes Promise.
      resolverRef.current?.(false);
      resolverRef.current = null;
    };
  }, []);

  const handleResult = (confirmed: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    resolve?.(confirmed);
  };

  return <B2BConfirmDialog key={requestSeq} open={open} subject={subject} onResult={handleResult} />;
};
